// Maybe temporary, could in theory be combined with filtered orders blotter

import { BLOTTER_ID_OPEN_ORDERS } from '@src/containers/Blotters/Orders/tokens';
import { getTradingOrderDetailsRoute } from '@src/containers/Routes/routes';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useOrders } from '@src/providers';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import {
  Accordion,
  AccordionBody,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterDensity,
  BlotterTable,
  BlotterTablePauseButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  FilterBuilder,
  FilterBuilderClearAllButton,
  FilterBuilderToggleButton,
  FilterInput,
  HStack,
  MixpanelEvent,
  Portal,
  SummaryLine,
  filterByColumnMainMenuItems,
  getOrderAmount,
  getOrderQuantity,
  isGridApiReady,
  isOrderComplete,
  orderStatusFilterTextToBackendStatus,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTable,
  useMixpanel,
  useObservable,
  usePersistedBlotterTable,
  type Order,
  type UnifiedLiquidityEnum,
} from '@talos/kyoko';
import type { GetMainMenuItemsParams, SelectionChangedEvent } from 'ag-grid-enterprise';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { map, shareReplay } from 'rxjs/operators';
import { useCurrentAppLayoutId } from '../../../hooks/useAppLayoutId';
import { useNavigateToTradingOrderDetails } from '../../../hooks/useNavigateToTradingOrderDetails';
import { useOrderColumns } from './Columns';
import { OrderOptions } from './OrderOptions';
import { colIDToFilterBuilderKey, useOrderFilter, type BlotterTableOrderFilter } from './useOrderFilter';
import { useOrderMenu } from './useOrderMenu';

interface OpenOrdersParams {
  defaultFilter: BlotterTableOrderFilter;
}

export function OpenOrders({ defaultFilter }: OpenOrdersParams) {
  const mixpanel = useMixpanel();
  const { recentOrders, recentOrdersError } = useOrders();
  const layoutId = useCurrentAppLayoutId();
  const liveOrders = useObservable(
    () =>
      recentOrders.pipe(
        // Build the active-order set fresh on every emission. The provider's Map
        // is authoritative (it now evicts stale open entries on OPEN_ORDERS reconnect),
        // so anything not present in the incoming array should not appear here either.
        map(orders => {
          const data = orders.filter(d => !isOrderComplete(d.OrdStatus));
          return {
            data,
            initial: true,
            type: 'Order',
          };
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      ),
    [recentOrders]
  );
  const { homeCurrency } = useDisplaySettings();
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);

  const columns = useOrderColumns({});

  const persisted = usePersistedBlotterTable(BLOTTER_ID_OPEN_ORDERS, { filter: defaultFilter });

  const filteredOrders = useOrderFilter({
    initialFilter: persisted.initialState?.filter,
    saveFilter: persisted.onFilterChanged,
    includeStatusFilter: false,
    includeIDFilter: false,
  });
  const { clientSideFilter, changeFilter, filterableProperties, initialFilterClauses } = filteredOrders;

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<Order>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    const selectedRows = api.getSelectedRows();
    setSelectedOrders(selectedRows);
  }, []);

  const { filterBuilder, accordion, openClause } = useAccordionFilterBuilder({
    accordionProps: {},
    filterBuilderProps: {
      initialFilterClauses,
      properties: filterableProperties,
    },
  });

  const { filterClausesByPropertyKey, filterClauses, removeAllFilterClauses } = filterBuilder;

  const getExtraMainMenuItems = useCallback(
    (params: GetMainMenuItemsParams) => {
      return filterByColumnMainMenuItems({
        params,
        colIDToFilterBuilderKey,
        openClause,
        mixpanel,
      });
    },
    [mixpanel, openClause]
  );

  const { getContextMenuItems, dialogComponents } = useOrderMenu({
    openClause,
    filterableProperties,
    generateOrderDetailsRoute: getTradingOrderDetailsRoute,
    layoutId,
  });

  const { showBlotterPauseButton } = useFeatureFlag();
  const onOpenDetails = useNavigateToTradingOrderDetails();

  const blotterTable = useBlotterTable<Order>({
    dataObservable: liveOrders,
    errorObservable: recentOrdersError,
    rowID: 'OrderID',
    density: BlotterDensity.Compact,
    clientLocalFilter: clientSideFilter,
    getExtraMainMenuItems,
    persistence: persisted,
    columns,
    pauseParams: {
      showPauseButton: showBlotterPauseButton,
    },
    gridOptions: {
      // There are a few heavy valueGetters in this blotter. Enabling the value cache improves performance.
      valueCache: true,
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems,
      onRowDoubleClicked: params => {
        const data = params.data;
        if (data) {
          onOpenDetails(data.OrderID, 'order', 'details');
        }
      },
    },
    exportOptions: {
      name: 'OpenOrders',
    },
  });

  // Forward the parts of the filter being sent to the backend
  useEffect(() => {
    changeFilter(curr => {
      const newFilter = removeEmptyFilters({
        ...curr,
        Markets: filterClausesByPropertyKey.get('Markets')?.selections,
        SelectedMarkets: filterClausesByPropertyKey.get('SelectedMarkets')?.selections,
        TradedMarkets: filterClausesByPropertyKey.get('TradedMarkets')?.selections,
        Sides: filterClausesByPropertyKey.get('Sides')?.selections,
        Symbols: filterClausesByPropertyKey.get('Symbols')?.selections,
        // Save the UI bound statuses in _ key. Save the real OrdStatuses used as Statuses
        _statuses: filterClausesByPropertyKey.get('_statuses')?.selections,
        Statuses: orderStatusFilterTextToBackendStatus(filterClausesByPropertyKey.get('_statuses')?.selections),
        Strategies: filterClausesByPropertyKey.get('Strategies')?.selections,
        SubAccounts: filterClausesByPropertyKey.get('SubAccounts')?.selections,
        Users: filterClausesByPropertyKey.get('Users')?.selections,
        Warnings: filterClausesByPropertyKey.get('Warnings')?.selections,
        ProductTypes: filterClausesByPropertyKey.get('ProductTypes')?.selections,
        UnifiedLiquidity: filterClausesByPropertyKey.get('UnifiedLiquidity')?.selections as
          | UnifiedLiquidityEnum[]
          | undefined,
      });
      if (isEqual(curr, newFilter)) {
        return curr;
      }
      return newFilter;
    });
  }, [filterClausesByPropertyKey, filteredOrders.filter, changeFilter]);

  return (
    <>
      <Portal portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}>
        <HStack justifyContent="space-between" gap="spacing4">
          <FilterInput
            alwaysExpanded
            value={blotterTable.blotterTableFiltersProps.quickFilterText}
            onChange={val => blotterTable.blotterTableFiltersProps.onQuickFilterTextChanged(val)}
            mixpanelEvent={MixpanelEvent.FilterQuickSearch}
            tooltip="Filter orders"
          />
          <Divider orientation="vertical" m="spacing4" />
          <FilterBuilderClearAllButton
            removeAllFilterClauses={removeAllFilterClauses}
            disabled={filterClauses.length === 0}
          />
          <FilterBuilderToggleButton
            filterClauses={filterClauses}
            isOpen={accordion.isOpen}
            onClick={() => accordion.toggle()}
          />
          <Divider orientation="vertical" m="spacing4" />
          {blotterTable.blotterTableFiltersProps.showPauseButton && (
            <>
              <BlotterTablePauseButton
                paused={blotterTable.blotterTableFiltersProps.paused}
                pause={blotterTable.blotterTableFiltersProps.pause}
                resume={blotterTable.blotterTableFiltersProps.resume}
              />
              <Divider orientation="vertical" m="spacing4" />
            </>
          )}

          <OrderOptions
            changeFilter={changeFilter}
            filter={filteredOrders.filter}
            blotterTable={blotterTable}
            tabType="OpenOrders"
          />
        </HStack>
      </Portal>
      <Accordion {...accordion}>
        <AccordionBody>
          <FilterBuilder {...filterBuilder} />
        </AccordionBody>
      </Accordion>
      <BlotterTable {...blotterTable} />
      <SummaryLine
        homeCurrency={homeCurrency}
        rows={selectedOrders}
        getQuantity={getOrderQuantity}
        getAmount={getOrderAmount}
      />
      {dialogComponents}
    </>
  );
}
